import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Breadcrumbs from "../../components/pageProps/Breadcrumbs";

const Journal = () => {
  const location = useLocation();
  const [prevLocation, setPrevLocation] = useState("");

  useEffect(() => {
    if (location.state && location.state.data) {
      setPrevLocation(location.state.data);
    }
  }, [location]);

  return (
    <div className="max-w-container mx-auto px-4 py-8">
      <Breadcrumbs title="Journals" prevLocation={prevLocation} />
      
      <div className="pb-10 max-w-4xl mx-auto">
        {/* Profile Info Section */}
        <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 mb-10">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b pb-2">
            Who I Am
          </h2>
          
          <div className="space-y-4 text-gray-600 leading-relaxed text-base md:text-lg">
            <p>
              Based in New Delhi, India, <span className="font-semibold text-gray-800">Raj</span> is a government-certified
              freelance tour guide with over 30 years of experience
              creating immersive cultural and heritage experiences.
            </p>
            <p>
              With extensive knowledge of Indian history, architecture,
              religions, food culture, traditions and local life, Raj
              helps travelers discover India beyond the ordinary tourist trail.
            </p>
            <p>
              His storytelling approach combines historical insight,
              authentic local experiences and warm hospitality,
              allowing guests to connect deeply with each destination.
            </p>
            <p>
              Over the years he has led heritage walks, city tours,
              cultural immersions, food trails and private journeys
              across Delhi, Rajasthan and Northern India.
            </p>
            <p>
              Memorable experiences include exploring the vibrant lanes
              of Old Delhi, discovering Mughal architecture,
              attending Sufi music evenings, tasting local street food
              and visiting traditional art galleries.
            </p>
            <p>
              Raj has welcomed travelers from the USA, Europe,
              Australia, Israel, Singapore, Hong Kong and Japan,
              including solo travelers, families, diplomats,
              academics and travel writers.
            </p>
            <p className="border-l-4 border-amber-600 pl-4 italic bg-amber-50/50 py-2 rounded-r">
              Certified by the Ministry of Tourism and trained in
              First Aid & Safety Procedures, he ensures every tour
              is informative, safe, comfortable and unforgettable.
            </p>
          </div>
        </div>

        {/* Tour Container Section */}
        <div className="bg-orange-50/30 border border-orange-100 p-6 md:p-8 rounded-xl shadow-sm mb-10">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-black text-amber-900 mb-2 tracking-wide uppercase">
              Old Delhi Highlights Tour
            </h1>
            <span className="inline-block bg-amber-700 text-white text-xs md:text-sm px-4 py-1.5 rounded-full font-bold tracking-wider uppercase shadow-sm">
              Flexible: Morning or Evening Tour
            </span>
          </div>

          {/* Timeline Wrapper */}
          <div className="relative border-l-2 border-amber-200 ml-4 md:ml-6 space-y-8">
            
            {/* Step 1 */}
            <div className="relative pl-6 md:pl-8 group">
              <div className="absolute -left-[9px] top-1.5 bg-amber-800 w-4 h-4 rounded-full border-4 border-white group-hover:bg-orange-500 transition-colors duration-300 shadow-sm"></div>
              <div>
                <h3 className="text-lg font-bold text-amber-950 mb-1 group-hover:text-amber-700 transition-colors">
                  Hotel Pickup & Arrival
                </h3>
                <p className="text-gray-600 text-sm md:text-base">
                  Convenient pickup from your hotel and direct drive to the historic <span className="text-orange-700 font-semibold">Sunehri Masjid</span> (Golden Mosque) near the Red Fort parking area.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative pl-6 md:pl-8 group">
              <div className="absolute -left-[9px] top-1.5 bg-amber-800 w-4 h-4 rounded-full border-4 border-white group-hover:bg-orange-500 transition-colors duration-300 shadow-sm"></div>
              <div>
                <h3 className="text-lg font-bold text-amber-950 mb-1 group-hover:text-amber-700 transition-colors">
                  The Iconic Red Fort
                </h3>
                <p className="text-gray-600 text-sm md:text-base">
                  A short walk to the <span className="text-orange-700 font-semibold">Red Fort</span> to witness the sheer scale and beauty of Delhi's most massive historic fort <span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded font-bold ml-1 inline-block">(Photo Stop Only)</span>.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative pl-6 md:pl-8 group">
              <div className="absolute -left-[9px] top-1.5 bg-amber-800 w-4 h-4 rounded-full border-4 border-white group-hover:bg-orange-500 transition-colors duration-300 shadow-sm"></div>
              <div>
                <h3 className="text-lg font-bold text-amber-950 mb-1 group-hover:text-amber-700 transition-colors">
                  Traditional Rickshaw Ride to Jama Masjid
                </h3>
                <p className="text-gray-600 text-sm md:text-base">
                  Hop onto a traditional <span className="text-orange-700 font-semibold">man-paddled rickshaw</span> to reach the grand <span className="text-orange-700 font-semibold">Jama Mosque</span>, built under the visionary supervision of Emperor Shah Jahan.
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="relative pl-6 md:pl-8 group">
              <div className="absolute -left-[9px] top-1.5 bg-amber-800 w-4 h-4 rounded-full border-4 border-white group-hover:bg-orange-500 transition-colors duration-300 shadow-sm"></div>
              <div>
                <h3 className="text-lg font-bold text-amber-950 mb-1 group-hover:text-amber-700 transition-colors">
                  Heritage Walking Tour
                </h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  Explore and capture authentic photos of Chandni Chowk's famous alleyways: 
                  <span className="font-semibold text-gray-800 block md:inline md:ml-1">Kinari Bazaar (Wedding Street),</span>
                  <span className="font-semibold text-gray-800 block md:inline md:ml-1">Dariba Kalan (Jewelry Market),</span> and the iconic 
                  <span className="font-semibold text-gray-800 block md:inline md:ml-1">Paranthe Wali Gali (Indian Eatery Street).</span>
                </p>
              </div>
            </div>

            {/* Step 5 */}
            <div className="relative pl-6 md:pl-8 group">
              <div className="absolute -left-[9px] top-1.5 bg-amber-800 w-4 h-4 rounded-full border-4 border-white group-hover:bg-orange-500 transition-colors duration-300 shadow-sm"></div>
              <div>
                <h3 className="text-lg font-bold text-amber-950 mb-1 group-hover:text-amber-700 transition-colors">
                  Masala Chai Experience
                </h3>
                <p className="text-gray-600 text-sm md:text-base">
                  Take a rickshaw again to pause and savor the world-famous <span className="text-orange-700 font-semibold">Masala Chai</span> (spiced tea) served traditionally in a <span className="text-orange-700 font-semibold">Kulhad</span> (clay pot).
                </p>
              </div>
            </div>

            {/* Step 6 */}
            <div className="relative pl-6 md:pl-8 group">
              <div className="absolute -left-[9px] top-1.5 bg-amber-800 w-4 h-4 rounded-full border-4 border-white group-hover:bg-orange-500 transition-colors duration-300 shadow-sm"></div>
              <div>
                <h3 className="text-lg font-bold text-amber-950 mb-1 group-hover:text-amber-700 transition-colors">
                  Khari Baoli (Spice Market)
                </h3>
                <p className="text-gray-600 text-sm md:text-base">
                  Ride deep into <span className="text-orange-700 font-semibold">Asia's largest wholesale spice market</span>. Immerse your senses in the vibrant colors and rich aromas of authentic Indian spices.
                </p>
              </div>
            </div>

            {/* Step 7 */}
            <div className="relative pl-6 md:pl-8 group">
              <div className="absolute -left-[9px] top-1.5 bg-amber-800 w-4 h-4 rounded-full border-4 border-white group-hover:bg-orange-500 transition-colors duration-300 shadow-sm"></div>
              <div>
                <h3 className="text-lg font-bold text-amber-950 mb-1 group-hover:text-amber-700 transition-colors">
                  Return Journey
                </h3>
                <p className="text-gray-600 text-sm md:text-base">
                  Drive back to the parking spot and board your private car for a comfortable drop-off back to your hotel.
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* Action Button */}
        <div className="mt-8 text-center md:text-left">
          <Link to="/memories">
            <button className="w-52 h-12 bg-amber-800 text-white font-medium rounded shadow-sm hover:bg-black transition-colors duration-300 uppercase tracking-wider text-sm">
              Continue Tour 
            </button>
          </Link>
        </div>

      </div>
    </div>
  );
};

export default Journal;